import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

import { AffiliateCard } from "@/components/settings/AffiliateCard";
import { PublicProfileCard } from "@/components/settings/PublicProfileCard";
import { UsernameCard } from "@/components/settings/UsernameCard";
import { WorkspaceSettingsTab } from "@/components/settings/WorkspaceSettingsTab";
import { DataAtRestCard } from "@/components/settings/DataAtRestCard";
import { MembershipsCard } from "@/components/settings/MembershipsCard";
import { NotificationPreferencesCard } from "@/components/settings/NotificationPreferencesCard";
import { AccountIdentityCard } from "@/components/settings/AccountIdentityCard";
import { SubscriptionCard } from "@/components/settings/SubscriptionCard";
import { WorkspaceMembersOverviewCard } from "@/components/settings/WorkspaceMembersOverviewCard";
import { AccountDangerZoneCard } from "@/components/settings/AccountDangerZoneCard";
import { WorkspaceAddonsCard } from "@/components/settings/WorkspaceAddonsCard";
import { AiUsageMeterCard } from "@/components/settings/AiUsageMeterCard";
import { McpConnectedAppsCard } from "@/components/settings/McpAccessCard";
import { McpConnectionsCard } from "@/components/settings/McpConnectionsCard";

import { AddonShortfallBanner } from "@/components/AddonShortfallBanner";
import { tierEntitlements } from "@/lib/tier";
import { toast } from "sonner";
import { AppSeo } from "@/components/AppSeo";

const ACCOUNT_TAB = "__account__";

const Settings = () => {
  const { user } = useAuth();
  const { active, workspaces, setActive } = useWorkspace();
  const [tab, setTab] = useState<string>(ACCOUNT_TAB);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Top-up return handler: Stripe redirects to /settings?topup=success&session_id=…
  // after a credit pack purchase. Show a toast, refresh the credits meter,
  // then strip the params from the URL.
  useEffect(() => {
    if (searchParams.get("topup") !== "success") return;
    toast.success("Credits added to your account", {
      description: "Your top-up balance is updated below.",
    });
    queryClient.invalidateQueries({ queryKey: ["account-ai-usage"] });
    queryClient.invalidateQueries({ queryKey: ["account-credit-history"] });
    const next = new URLSearchParams(searchParams);
    next.delete("topup");
    next.delete("session_id");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, queryClient]);

  const primary = useMemo(
    () => workspaces.find((w) => w.is_primary) ?? workspaces[0] ?? null,
    [workspaces],
  );
  const accountTier = primary?.account_tier ?? null;
  const ent = tierEntitlements(accountTier);

  // Deep-link from the credits notification: ?focus=ai-credits switches to
  // the primary workspace tab (where AiUsageMeterCard lives) and lets the
  // card itself scroll into view. The param is left in the URL so the card's
  // own effect can react to it; the card clears it after scrolling.
  // Deep-link from the credits notification: ?focus=ai-credits switches to
  // the Account tab (where AiUsageMeterCard now lives) and lets the card
  // itself scroll into view. The param stays in the URL so the card's own
  // effect can react to it; the card clears it after scrolling.
  useEffect(() => {
    if (searchParams.get("focus") !== "ai-credits") return;
    if (tab !== ACCOUNT_TAB) setTab(ACCOUNT_TAB);
  }, [searchParams, tab]);

  // When the user clicks a workspace tab, reflect that selection in the
  // global active-workspace state so the workspace-scoped components
  // (TagCategoryManager, ReadwiseIntegrationCard) read
  // the right workspace.
  useEffect(() => {
    if (tab === ACCOUNT_TAB) return;
    if (active?.id !== tab && workspaces.some((w) => w.id === tab)) {
      setActive(tab);
    }
  }, [tab, active?.id, workspaces, setActive]);

  if (!primary) return null;
  void user;

  return (
    <>
    <AppSeo title="Settings" />
    <div className="max-w-3xl space-y-6">
      <h1 className="font-serif text-3xl font-semibold">Settings</h1>

      <AddonShortfallBanner />

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="flex w-full flex-wrap justify-start gap-1 overflow-x-auto">
          <TabsTrigger value={ACCOUNT_TAB}>Account</TabsTrigger>
          {workspaces.map((w, idx) => (
            <TabsTrigger key={w.id} value={w.id} className="flex items-center gap-2">
              {w.workspace_label?.trim() || `Workspace ${idx + 1}`}
              {w.account_tier === "admin_workspace" && (
                <Badge variant="secondary" className="text-[10px] py-0">admin</Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={ACCOUNT_TAB} className="space-y-6">
          <AccountIdentityCard primary={primary} />

          <UsernameCard />

          <AiUsageMeterCard workspace={primary} />

          <PublicProfileCard />

          <DataAtRestCard />

          <MembershipsCard />

          <WorkspaceMembersOverviewCard />

          <NotificationPreferencesCard />

          {ent.affiliateAvailable ? <AffiliateCard /> : null}

          <SubscriptionCard accountTier={accountTier} />

          <WorkspaceAddonsCard />

          <McpConnectedAppsCard />

          <AccountDangerZoneCard />
        </TabsContent>

        {workspaces.map((w) => (
          <TabsContent key={w.id} value={w.id} className="space-y-6">
            {tab === w.id && <WorkspaceSettingsTab workspace={w} />}
          </TabsContent>
        ))}
      </Tabs>
    </div>
    </>
  );
};

export default Settings;
